'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faBookmark,
  faFolderBookmark,
  faCheck,
  faClose,
} from '@fortawesome/pro-solid-svg-icons';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { useParams } from 'next/navigation';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { useGetFilterSetByIdQuery } from 'src/api/redux/filterSets';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';
import {
  useCodeSetSelectorData,
  CodeSetItemRow,
  CodeSetSelectorSection,
  CodeSetSelectorFolder,
} from 'src/components/CodeSetSelector';
import { useFlags } from 'launchdarkly-react-client-sdk';
interface ProfileSelectorDropdownProps {
  location: string;
}

const ProfileSelectorDropdown: React.FC<ProfileSelectorDropdownProps> = ({
  location = 'hcp',
}) => {
  const { useProfileTreatingVolume } = useFlags();
  const { view } = useParams<{ view: string }>();
  const {
    selectedFilterSet,
    selectedCohort,
    setSelectedFilterSet,
    setSelectedCohort,
    clearSelectedProfile,
    setAllCodeSets,
    hasSelection,
    isAllCodeSets,
  } = useProfileSelections(location);

  // Routes where encounter items should be disabled
  const chartViewRoutes = [
    'cpt',
    'drg',
    'hcpcs',
    'icdp',
    'prescriptions',
    'icd',
    'apc',
  ];
  const shouldDisableEncounterItems = chartViewRoutes.includes(
    view as string
  );

  const [isOpen, setIsOpen] = useState(false);
  const [expandedFolder, setExpandedFolder] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string | null>(
    'company'
  );
  const [maxDropdownHeight, setMaxDropdownHeight] = useState<
    number | undefined
  >(undefined);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  // Shared data layer: fetch + folder detection + live-status resolution +
  // deactivated exclusion. Profile shows every folder member regardless of scope
  // and filters to CODE filter sets. See src/components/CodeSetSelector.
  const { ungroupedItems, folders, isLoading } = useCodeSetSelectorData({
    filterSetArgs: { serializer: 'detailed' },
    filterSetType: 'CODE',
    matchFolderItemScope: false,
    deactivatedPolicy: 'exclude',
  });

  // Get fresh data for the currently selected filter set to ensure it's up-to-date
  const filterSetId = selectedFilterSet?.id?.toString() || '';
  const { data: freshSelectedFilterSet } = useGetFilterSetByIdQuery(
    { id: filterSetId },
    { skip: !filterSetId }
  );

  // Update the profile selections with fresh data when available
  useEffect(() => {
    if (
      freshSelectedFilterSet &&
      selectedFilterSet?.id === freshSelectedFilterSet.id
    ) {
      // Only update if the data is actually different to avoid unnecessary updates
      if (
        JSON.stringify(selectedFilterSet.filters) !==
        JSON.stringify(freshSelectedFilterSet.filters)
      ) {
        setSelectedFilterSet(freshSelectedFilterSet);
      }
    }
  }, [freshSelectedFilterSet, selectedFilterSet, setSelectedFilterSet]);

  // Organize the shared data hook's output into scoped sections for rendering.
  // Items are the raw cohort / filter set records (what ProfileSelectorItem
  // expects); status resolution + deactivated exclusion happened in the hook.
  const organizedData = useMemo(() => {
    const data = {};
    const sectionMap = {}; // Track original case for display

    const ensureSection = (originalScope: string) => {
      const scopeKey = originalScope.toLowerCase();
      if (!data[scopeKey]) {
        data[scopeKey] = { folders: [], ungroupedItems: [] };
        sectionMap[scopeKey] = originalScope;
      }
      return scopeKey;
    };

    const byLabel = (a, b) => (a.label || '').localeCompare(b.label || '');

    // Organize folders by scope. Items render filter sets first, then cohorts.
    folders.forEach((folder) => {
      const scopeKey = ensureSection(folder.scope || 'Unknown');

      const folderFilterSets = folder.items
        .filter((item) => item.kind === 'filterSet')
        .map((item) => item.raw)
        .sort(byLabel);
      const folderCohorts = folder.items
        .filter((item) => item.kind === 'cohort')
        .map((item) => item.raw)
        .sort(byLabel);

      data[scopeKey].folders.push({
        ...folder,
        items: [...folderFilterSets, ...folderCohorts],
      });
    });

    Object.keys(data).forEach((scopeKey) => {
      data[scopeKey].folders.sort(byLabel);
    });

    // Ungrouped items to their respective scopes.
    ungroupedItems.forEach((item) => {
      const scopeKey = ensureSection(item.scope || 'Unknown');
      data[scopeKey].ungroupedItems.push(item.raw);
    });

    Object.keys(data).forEach((scopeKey) => {
      data[scopeKey].ungroupedItems.sort(byLabel);
    });

    // Extract sections with original case, Company first, then alphabetically.
    const sortedSections = Object.keys(data)
      .sort((a, b) => {
        if (a === 'company' && b !== 'company') return -1;
        if (b === 'company' && a !== 'company') return 1;
        return a.localeCompare(b);
      })
      .map((key) => sectionMap[key]);

    return {
      sections: sortedSections,
      data: data,
    };
  }, [folders, ungroupedItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate max dropdown height based on viewport position and header state
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const calculateMaxHeight = () => {
        if (!dropdownRef.current) return;

        const rect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate available space below the dropdown button
        // Use a 15px buffer for comfortable spacing
        const availableHeight = viewportHeight - rect.bottom - 15;

        const maxAllowedHeight = 800;

        // Use almost all available space, with a minimum height of 200px
        const maxHeight = Math.max(
          200,
          Math.min(maxAllowedHeight, availableHeight)
        );

        setMaxDropdownHeight(maxHeight);
      };

      calculateMaxHeight();
      // Also update on resize
      window.addEventListener('resize', calculateMaxHeight);

      return () => {
        window.removeEventListener('resize', calculateMaxHeight);
      };
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemSelection = useCallback(
    (item: any) => {
      if (!item.type && !item.filters) {
        setSelectedCohort(item);
      } else {
        setSelectedFilterSet(item);
      }
      setIsOpen(false);
    },
    [setSelectedCohort, setSelectedFilterSet]
  );

  const handleClearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearSelectedProfile();
    },
    [clearSelectedProfile]
  );

  // Handler for "Search without Codes" selection
  const handleSearchWithoutCodes = () => {
    clearSelectedProfile(); // This sets both filter_set and cohort to null
    setIsOpen(false);
  };

  const handleAllCodeSetsSelection = () => {
    setAllCodeSets();
    setIsOpen(false);
  };

  const toggleFolder = (folderId: string) => {
    const isExpanded = expandedFolder === folderId;
    if (isExpanded) {
      setExpandedFolder(null);
    } else {
      setExpandedFolder(folderId);
    }
  };

  const toggleSection = (sectionKey: string) => {
    // If the clicked section is already expanded, collapse it
    if (expandedSections === sectionKey) {
      setExpandedSections(null);
    } else {
      // Otherwise, expand the clicked section (and collapse any other expanded section)
      setExpandedSections(sectionKey);
    }
  };

  // Updated label logic for new selection model - includes null state and All Code Sets
  const label = selectedFilterSet
    ? selectedFilterSet.label
    : selectedCohort
    ? selectedCohort.label
    : isAllCodeSets
    ? 'All Code Sets'
    : 'Search without Codes';

  const hasValue = hasSelection() || isAllCodeSets;
  // Updated icon logic - faGlobe for null state (Search without Codes), faFolderBookmark for All Code Sets
  const icon = !hasValue
    ? faGlobe
    : isAllCodeSets
    ? faFolderBookmark
    : faBookmark;
  return (
    <div
      className="relative w-full max-w-96 self-stretch inline-flex flex-col justify-start items-start gap-1"
      ref={dropdownRef}
      data-testid="profile-selector-dropdown"
    >
      {/* Dropdown Button */}
      <div
        className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1
        outline-neutral-300 inline-flex justify-between gap-2 items-center cursor-pointer hover:outline-neutral-400"
        onClick={toggleDropdown}
      >
        <div className="flex items-center justify-between w-full gap-2">
          <div className="inline-flex justify-start items-center gap-2 flex-1 min-w-0">
            <div className="w-5 h-5 overflow-hidden flex items-center justify-center flex-shrink-0">
              <Icon
                icon={icon}
                className={
                  !hasValue
                    ? 'w-4 h-4 text-neutral-600'
                    : 'w-3 h-3 text-neutral-600'
                }
              />
            </div>

            <div
              className={`justify-start text-base font-normal font-['Inter'] leading-normal truncate ${
                hasValue ? 'text-primary' : 'text-neutral-500'
              }`}
            >
              {isLoading ? 'Loading...' : label}
            </div>
          </div>
          <div
            className={`flex items-center gap-2 flex-shrink-0 transition-all duration-300
              ${hasValue ? 'opacity-100' : 'opacity-0'}
              `}
            onClick={handleClearSelection}
          >
            <Icon
              icon={faClose}
              className="w-4 h-4 text-neutral-600 hover:text-neutral-800"
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Icon
              icon={faCaretDown}
              className={`text-neutral-600 w-4 h-4 transition-all duration-300
                        ${isOpen ? '-rotate-90' : 'rotate-0'}
                        `}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && !isLoading && (
        <div
          ref={dropdownMenuRef}
          className="absolute top-12 inline-flex flex-col justify-start items-start
          w-96 bg-white rounded-xl shadow-sm outline outline-1 -outline-offset-1 outline-neutral-300
          overflow-y-auto z-50 pb-2"
          style={{
            maxHeight: maxDropdownHeight ? `${maxDropdownHeight}px` : undefined,
          }}
        >
          <div className="self-stretch h-12 px-4 border-b border-neutral-200 inline-flex justify-between items-center">
            <div className="flex justify-center items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <Icon
                  icon={faFolderBookmark}
                  className="w-4 h-3.5 text-neutral-600"
                />
              </div>
              <div className="justify-start text-primary text-sm font-bold font-['Inter'] leading-tight">
                Code Sets
              </div>
            </div>
          </div>
          <div
            className={`self-stretch h-10 px-4 border-b border-neutral-200 inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100 ${
              !hasValue && !isAllCodeSets ? 'bg-neutral-50' : ''
            }`}
            onClick={handleSearchWithoutCodes}
            data-testid="search-without-codes-option"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <Icon icon={faGlobe} className="w-4 h-3.5 text-neutral-600" />
            </div>
            <div
              className={`flex-1 justify-start text-sm font-semibold font-['Inter'] leading-tight ${
                !hasValue && !isAllCodeSets ? 'text-sky-600' : 'text-primary'
              }`}
            >
              Search without Codes
            </div>
            {!hasValue && !isAllCodeSets && (
              <div className="w-4 h-4 flex items-center justify-center">
                <Icon icon={faCheck} className="w-3 h-3 text-sky-600" />
              </div>
            )}
          </div>
          {useProfileTreatingVolume && (
            <div
              className={`self-stretch h-10 px-4 border-b border-neutral-200 inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100 ${
                isAllCodeSets ? 'bg-neutral-50' : ''
              }`}
              onClick={handleAllCodeSetsSelection}
              data-testid="all-code-sets-option"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <Icon
                  icon={faFolderBookmark}
                  className="w-4 h-3.5 text-neutral-600"
                />
              </div>
              <div
                className={`flex-1 justify-start text-sm font-semibold font-['Inter'] leading-tight ${
                  isAllCodeSets ? 'text-sky-600' : 'text-primary'
                }`}
              >
                All Code Sets
              </div>
              {isAllCodeSets && (
                <div className="w-4 h-4 flex items-center justify-center">
                  <Icon icon={faCheck} className="w-3 h-3 text-sky-600" />
                </div>
              )}
            </div>
          )}
          <div className="self-stretch overflow-hidden flex flex-col justify-start items-start w-full">
            {organizedData.sections.map((section) => {
              const lowercaseKey = section.toLowerCase();
              const sectionData = organizedData.data[lowercaseKey];
              const isSectionExpanded = expandedSections === lowercaseKey;

              return (
                <CodeSetSelectorSection
                  key={section}
                  label={sentenceCase(section)}
                  isOpen={isSectionExpanded}
                  onToggle={() => toggleSection(lowercaseKey)}
                  contentClassName="px-2 overflow-y-auto max-h-60"
                  data-testid={`profile-section-header-${lowercaseKey}`}
                >
                  {/* Folders in this section */}
                  {sectionData?.folders?.map((folder, index) => {
                    const isExpanded = expandedFolder === folder.id?.toString();
                    return (
                      <CodeSetSelectorFolder
                        key={`${folder.id}-${index}`}
                        label={folder.label}
                        isOpen={isExpanded}
                        onToggle={() => toggleFolder(folder.id?.toString())}
                        data-testid={`profile-selector-folder-${folder.id}`}
                      >
                        {folder.items.map((item, itemIndex) => {
                          const isSelected =
                            (selectedFilterSet &&
                              selectedFilterSet.id === item.id) ||
                            (selectedCohort && selectedCohort.id === item.id);
                          const isDisabled =
                            shouldDisableEncounterItems && !item.type;
                          return (
                            <CodeSetItemRow
                              key={`${item.id}-${itemIndex}`}
                              label={item.label}
                              kind={item.type ? 'filterSet' : 'cohort'}
                              status={item.status}
                              isSelected={isSelected}
                              disabled={isDisabled}
                              disabledReason="Cannot filter by encounter items in this view."
                              onSelect={() => handleItemSelection(item)}
                              data-testid={`profile-selector-item-${item.id}`}
                            />
                          );
                        })}
                      </CodeSetSelectorFolder>
                    );
                  })}

                  {/* Ungrouped items in this section - displayed directly */}
                  {sectionData?.ungroupedItems?.map((item, index) => {
                    const isSelected =
                      (selectedFilterSet &&
                        selectedFilterSet.id === item.id) ||
                      (selectedCohort && selectedCohort.id === item.id);
                    const isDisabled =
                      shouldDisableEncounterItems && !item.type;
                    return (
                      <CodeSetItemRow
                        key={`${item.id}-${index}`}
                        label={item.label}
                        kind={item.type ? 'filterSet' : 'cohort'}
                        status={item.status}
                        isSelected={isSelected}
                        disabled={isDisabled}
                        disabledReason="Cannot filter by encounter items in this view."
                        onSelect={() => handleItemSelection(item)}
                        data-testid={`profile-selector-item-${item.id}`}
                      />
                    );
                  })}

                  {/* Empty state */}
                  {!sectionData?.folders?.length &&
                    !sectionData?.ungroupedItems?.length && (
                      <div className="group self-stretch px-4 py-2 inline-flex justify-start items-center gap-2">
                        <div className="flex-1 justify-start text-neutral-500 pl-7 text-base font-normal font-['Inter'] leading-normal">
                          No profiles available
                        </div>
                      </div>
                    )}
                </CodeSetSelectorSection>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSelectorDropdown;
